// pch-topbar-guard.js
// -----------------------------------------------------------------------------
// Two small, mobile-scoped safety nets for the top of the page.
//
// 1) Sticky-header protection.
//    A third-party app (OptiMonk's spin-to-win popup) locks page scroll by setting
//    `overflow: hidden` INLINE on <html> and <body>, and can leave that lock behind
//    after its popup is minimised to a teaser. An ancestor with `overflow: hidden`
//    breaks `position: sticky`, so this silently stops the header pinning and it
//    collides with the Yeps promo bar.
//
//    The theme's OWN scroll-lock never touches inline root overflow: dialogs use
//    `html[scroll-lock] { overflow: hidden }` (attribute-driven CSS) plus a
//    `body { position: fixed }` freeze (see dialog.js). So any INLINE
//    `overflow: hidden` on <html>/<body> while the `scroll-lock` attribute is absent
//    is a foreign leak and is safe to clear — which restores the sticky header.
//
// 2) Drawer clearance.
//    Exposes `--pch-topbar-offset`: the height of the Yeps bar currently visible at
//    the top of the viewport. The fixed mobile menu drawer reads it so its close
//    button and first item start below the bar instead of behind it.
//
// Desktop is unaffected — both behaviours are gated to the mobile breakpoint.

(() => {
  const root = document.documentElement;
  const body = document.body;
  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;

  // Height of the top bar space the theme reserves (Yeps sets body margin-top).
  // Cached so scroll handling stays cheap; refreshed when the body actually mutates.
  let reservedTopBar = 0;
  const readReservedTopBar = () => {
    reservedTopBar = parseFloat(getComputedStyle(body).marginTop) || 0;
  };

  // (1) Clear a foreign inline overflow lock that would break the sticky header.
  const clearForeignScrollLock = () => {
    if (!isMobile()) return;
    // Leave the theme's genuine scroll-lock (menu drawer / cart / search) alone.
    if (root.hasAttribute('scroll-lock')) return;
    if (root.style.overflow === 'hidden') root.style.removeProperty('overflow');
    if (body.style.overflow === 'hidden') body.style.removeProperty('overflow');
  };

  // (2) Publish the currently-visible top-bar height for the drawer to offset by.
  const updateTopBarOffset = () => {
    const visible = isMobile() ? Math.max(0, reservedTopBar - (window.scrollY || 0)) : 0;
    const value = `${Math.round(visible)}px`;
    if (root.style.getPropertyValue('--pch-topbar-offset') !== value) {
      root.style.setProperty('--pch-topbar-offset', value);
    }
  };

  const scrollElement = document.scrollingElement || root;
  const onScroll = () => {
    // scrollY doesn't reflect the locked position while a drawer freezes scroll,
    // so fall back to the scrolling element's scrollTop.
    const scrolled = window.scrollY || scrollElement.scrollTop || 0;
    const visible = isMobile() ? Math.max(0, reservedTopBar - scrolled) : 0;
    const value = `${Math.round(visible)}px`;
    if (root.style.getPropertyValue('--pch-topbar-offset') !== value) {
      root.style.setProperty('--pch-topbar-offset', value);
    }
  };

  // React to attribute changes on <html>/<body>: OptiMonk toggles inline overflow,
  // Yeps toggles the body margin / html classes. Only re-read the (costly) reserved
  // height when <body> itself mutated; <html> changes (incl. our own var writes)
  // just run the cheap lock check.
  const observer = new MutationObserver((records) => {
    clearForeignScrollLock();
    for (const record of records) {
      if (record.target === body) {
        readReservedTopBar();
        updateTopBarOffset();
        break;
      }
    }
  });
  observer.observe(root, { attributes: true, attributeFilter: ['style', 'class'] });
  observer.observe(body, { attributes: true, attributeFilter: ['style', 'class'] });

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
    readReservedTopBar();
    clearForeignScrollLock();
    updateTopBarOffset();
  }, { passive: true });

  const init = () => {
    readReservedTopBar();
    clearForeignScrollLock();
    updateTopBarOffset();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
